import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import { Search, X, ArrowRight, ChevronRight, Home, Users, FileText, Receipt, ListTodo, Briefcase, Calendar, Bell, Settings, ShieldCheck, Send, MessageCircle, PenTool, Globe2, BarChart3, BookOpen, UserPlus, FileBarChart, Inbox } from "lucide-react";

/**
 * Iter83 — Universal Command Palette (Cmd+K / Ctrl+K).
 *
 * Features :
 *  - Liste de routes admin + actions rapides + clients (top 20).
 *  - Filtre fuzzy par titre / sous-titre.
 *  - Navigation clavier (↑/↓/Enter/Esc).
 *  - data-testid sur chaque element pour les tests E2E.
 *
 * Activation : window keydown listener dans MainLayout.jsx (Cmd+K / Ctrl+K).
 */

// Routes principales (toujours visibles)
const STATIC_ITEMS = [
  { id: "nav-dashboard", label: "Tableau de bord", subtitle: "Accueil admin", icon: Home, path: "/admin", group: "Navigation" },
  { id: "nav-clients", label: "Clients", subtitle: "Master — Registre central (Cabinet)", icon: Users, path: "/admin/masters", group: "Navigation" },
  { id: "nav-clients-new", label: "Nouveau client", subtitle: "Creer une fiche Master", icon: UserPlus, path: "/admin/masters", group: "Actions rapides" },

  // Finance
  { id: "nav-cabinet-taxes", label: "Taxes TPS/TVQ", subtitle: "Perçues, CTI/RTI, déclarations par période", icon: BookOpen, path: "/admin/cabinet-taxes", group: "Finance" },
  { id: "nav-aide", label: "Mode d'emploi et aide", subtitle: "Guides pas à pas · feuillet explicatif CCQ · PDF", icon: BookOpen, path: "/admin/aide", group: "Navigation" },
  { id: "nav-transactions-clients", label: "Transactions clients", subtitle: "Ventes, notes de crédit, encaissements, journal des ventes (cabinet)", icon: Receipt, path: "/admin/comptabilite/transactions-clients", group: "Finance" },
  { id: "nav-transactions-fournisseurs", label: "Transactions fournisseurs", subtitle: "Achats, notes de débit, décaissements, journal des achats (cabinet)", icon: BookOpen, path: "/admin/comptabilite/transactions-fournisseurs", group: "Finance" },
  { id: "nav-fournisseurs", label: "Fournisseurs", subtitle: "Fiches fournisseurs du cabinet", icon: BookOpen, path: "/admin/comptabilite/fournisseurs", group: "Finance" },
  { id: "nav-grand-livre", label: "Grand livre", subtitle: "Journal général, report au grand livre, grand livre par compte (cabinet)", icon: BookOpen, path: "/admin/comptabilite/grand-livre", group: "Finance" },
  { id: "nav-rapports-compta", label: "Rapports comptables", subtitle: "Balance, résultats, bilan, états financiers, journaux, âge des comptes (cabinet)", icon: BarChart3, path: "/admin/comptabilite/rapports", group: "Finance" },
  { id: "nav-plan-comptable", label: "Plan comptable", subtitle: "Comptes, rôles & réglages, soldes d'ouverture, numérotation (cabinet)", icon: BookOpen, path: "/admin/comptabilite/plan", group: "Finance" },
  { id: "nav-produits-services", label: "Produits & services", subtitle: "Catalogue : codes, prix, comptes GL (cabinet)", icon: FileText, path: "/admin/comptabilite/produits", group: "Finance" },
  { id: "nav-tenue-livres", label: "Tenue de livres", subtitle: "Clients, fournisseurs, ventes, achats, paiements et états financiers d'un dossier client", icon: BookOpen, path: "/admin/tenue-livres", group: "Finance" },
  { id: "nav-tax-lines", label: "Lignes fiscales", subtitle: "T1 / TP1 par client", icon: FileBarChart, path: "/admin/tax-lines", group: "Finance" },
  { id: "nav-reports", label: "Rapports", subtitle: "Rapports financiers", icon: BarChart3, path: "/admin/reports", group: "Finance" },

  // Documents
  { id: "nav-doc-sent", label: "Envois", subtitle: "Documents envoyes", icon: Send, path: "/admin/documents/sent", group: "Documents" },
  { id: "nav-doc-received", label: "Depots", subtitle: "Documents recus du portail", icon: FileText, path: "/admin/documents/received", group: "Documents" },
  { id: "nav-doc-tosign", label: "Documents à signer", subtitle: "Envoyer un modèle PDF", icon: PenTool, path: "/admin/client-documents", group: "Documents" },
  { id: "nav-comms-hub", label: "Suivi comms", subtitle: "Lectures factures / docs / mandats", icon: Inbox, path: "/admin/communications-hub", group: "Documents" },
  { id: "nav-messages", label: "Chat clients", subtitle: "Messagerie en direct", icon: MessageCircle, path: "/admin/questions", group: "Documents" },

  // Operations
  { id: "nav-tasks", label: "Tâches & check-listes", subtitle: "Suivi par dossier, étapes à cocher, Kanban", icon: ListTodo, path: "/admin/tasks", group: "Operations" },
  { id: "nav-appointments", label: "Rendez-vous", subtitle: "Calendrier RDV", icon: Calendar, path: "/admin/appointments", group: "Operations" },
  { id: "nav-portal-admin", label: "Portail clients", subtitle: "Comptes, accès & tuiles visibles", icon: Briefcase, path: "/admin/portail-clients", group: "Operations" },

  // IA

  // Systeme
  { id: "nav-notifications", label: "Notifications", subtitle: "Centre de notifications", icon: Bell, path: "/admin/notifications", group: "Systeme" },
  { id: "nav-security", label: "Securite", subtitle: "Tableau de bord securite (dans Parametres)", icon: ShieldCheck, path: "/admin/security", group: "Systeme" },
  { id: "nav-audit", label: "Piste d'audit", subtitle: "Qui a modifie quoi, et quand", icon: ShieldCheck, path: "/admin/audit", group: "Systeme" },
  { id: "nav-settings", label: "Parametres", subtitle: "Configuration cabinet", icon: Settings, path: "/admin/settings", group: "Systeme" },

  // Public
  { id: "nav-contact-public", label: "Page contact publique", subtitle: "Formulaire client public", icon: Globe2, path: "/contact-public", group: "Public" },
];

function fuzzyMatch(query, item) {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    item.label.toLowerCase().includes(q) ||
    (item.subtitle || "").toLowerCase().includes(q) ||
    item.group.toLowerCase().includes(q)
  );
}

export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [clients, setClients] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Charger les clients (top 50, on filtre cote front)
  useEffect(() => {
    if (!open || clients.length > 0) return;
    api.get("/clients").then((r) => {
      const arr = Array.isArray(r.data) ? r.data : [];
      setClients(arr.slice(0, 50));
    }).catch(() => setClients([]));
  }, [open, clients.length]);

  // Items dynamiques : on inclut les clients quand l'utilisateur tape
  const items = useMemo(() => {
    const all = [...STATIC_ITEMS];
    if (query.trim().length >= 2) {
      const q = query.trim().toLowerCase();
      const matchingClients = clients
        .filter((c) => {
          const fullName = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
          const email = (c.email || "").toLowerCase();
          return fullName.includes(q) || email.includes(q);
        })
        .slice(0, 8)
        .map((c) => ({
          id: `client-${c.id}`,
          label: `${c.first_name || ""} ${c.last_name || ""}`.trim() || "(sans nom)",
          subtitle: c.email || c.client_type || "Client",
          icon: User2Icon,
          path: `/admin/clients?open=${c.id}`,
          group: "Clients",
        }));
      all.push(...matchingClients);
    }
    return all.filter((it) => fuzzyMatch(query, it));
  }, [query, clients]);

  // Group items pour l'affichage
  const grouped = useMemo(() => {
    const groups = {};
    items.forEach((it) => {
      (groups[it.group] ||= []).push(it);
    });
    return groups;
  }, [items]);

  // Flat list dans l'ordre d'affichage (pour la navigation clavier)
  const flatItems = useMemo(() => {
    const flat = [];
    Object.values(grouped).forEach((arr) => flat.push(...arr));
    return flat;
  }, [grouped]);

  // Reset activeIdx quand le filtre change
  useEffect(() => { setActiveIdx(0); }, [query]);

  const runItem = useCallback((item) => {
    if (!item) return;
    onClose();
    if (item.path) navigate(item.path);
  }, [navigate, onClose]);

  // Navigation clavier
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(flatItems.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        runItem(flatItems[activeIdx]);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, flatItems, activeIdx, onClose, runItem]);

  // Scroll active item into view
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-cmd-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  if (!open) return null;

  let renderIdx = -1;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-[10vh] px-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="command-palette"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col overflow-hidden border">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher une page, un client, une action..."
            className="flex-1 outline-none text-sm placeholder:text-gray-400"
            data-testid="command-palette-input"
            aria-label="Recherche commande"
          />
          <kbd className="text-[10px] px-1.5 py-0.5 rounded border bg-gray-50 text-gray-500 font-mono">Esc</kbd>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 ml-1"
            aria-label="Fermer"
            data-testid="command-palette-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto" data-testid="command-palette-list">
          {flatItems.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-400">
              Aucun resultat pour <span className="font-medium">"{query}"</span>
            </div>
          ) : (
            Object.entries(grouped).map(([group, arr]) => (
              <div key={group}>
                <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {group}
                </div>
                <div>
                  {arr.map((it) => {
                    renderIdx += 1;
                    const myIdx = renderIdx;
                    const Icon = it.icon || ChevronRight;
                    const active = myIdx === activeIdx;
                    return (
                      <button
                        key={it.id}
                        onMouseEnter={() => setActiveIdx(myIdx)}
                        onClick={() => runItem(it)}
                        data-cmd-idx={myIdx}
                        data-testid={`cmd-item-${it.id}`}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-left transition ${
                          active ? "bg-blue-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          active ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-500"
                        }`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{it.label}</p>
                          {it.subtitle && (
                            <p className="text-xs text-gray-500 truncate">{it.subtitle}</p>
                          )}
                        </div>
                        {active && <ArrowRight className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t bg-gray-50 flex items-center justify-between text-[10px] text-gray-500">
          <div className="flex items-center gap-3">
            <span><kbd className="font-mono px-1 rounded border bg-white">↑</kbd> <kbd className="font-mono px-1 rounded border bg-white">↓</kbd> Naviguer</span>
            <span><kbd className="font-mono px-1 rounded border bg-white">Enter</kbd> Selectionner</span>
            <span><kbd className="font-mono px-1 rounded border bg-white">Esc</kbd> Fermer</span>
          </div>
          <span className="text-gray-400">
            {flatItems.length} resultat{flatItems.length > 1 ? "s" : ""}
          </span>
        </div>
      </div>
    </div>
  );
}

// Mini icone client (cercle utilisateur compact)
function User2Icon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a8 8 0 0 1 16 0v1" />
    </svg>
  );
}
